import { Component, HostListener, inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { OnInit, OnDestroy, viewChild, AfterViewInit, ElementRef } from '@angular/core';
import { CreditsService } from '../../../../core/services/credits.service';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { Utils } from '../../../../shared/utils';
import { trigger, transition, style, animate, state } from '@angular/animations';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { ViewChild } from '@angular/core';
import { BreakpointObserver,Breakpoints } from '@angular/cdk/layout';
import { LanguageService } from '../../../../core/services/language/language.service';


import { RouterModule } from '@angular/router';
import { CardSkeleton } from '../../../../layout/designs/card-skeleton/card-skeleton';

@Component({
  selector: 'app-movie-credits',
  imports: [
    CommonModule,
    RouterModule,
    CardSkeleton
  ],
  templateUrl: './movie-credits.html',
  styleUrl: './movie-credits.scss',
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('500ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ]),
    trigger('fadeAndToggle', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('500ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      state('active', style({ transform: 'scale(1.1)', background: 'linear-gradient(340deg, #96f2d7, #96f2d7, #38d9a9, #12b886)', borderRadius: '2.5em' })),
      state('inactive', style({ transform: 'scale(1)', backgroundColor: '#f8f9fa', borderRadius: '2.5em' })),
      transition('inactive => active', [
        animate('200ms cubic-bezier(.68,-0.55,.27,1.55)')
      ]),
      transition('active => inactive', [
        animate('200ms ease-out')
      ])
    ])
  ]
})
export class MovieCredits implements OnInit, AfterViewInit {


  isMobile: boolean = false;
  canScrollLeft: boolean = false;
  canScrollRight: boolean = false;
  activeCardIndex: number | null = null;
  creditData: any;
  movieId: any;
  movieName: any;
  frontendUrl: string = environment.frontendUrl;
  isLoading: boolean = true;
  initialSkeletons: number[] = Array(8).fill(0);
  viewportWidth: number = 0;
  private platformId = inject(PLATFORM_ID);
  isBlurred = false;
  private readonly destroy$ = new Subject<void>();

  private hoveredIndex: number | null = null;
  
  public readonly languageService = inject(LanguageService);

  constructor(
    private creditService: CreditsService, 
    private route: ActivatedRoute, 
    private utils: Utils,
    private breakpointObserver: BreakpointObserver
  ) { }
    @ViewChild('trendingMovieSection') trendingMovieSection!: ElementRef;

  ngOnInit(): void {
    this.breakpointObserver
      .observe([Breakpoints.Handset])
      .subscribe(result => {
        this.isMobile = result.matches;
      });

    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const slug = params.get('slug');
      this.getMovieCredits(slug);
    });

    this.languageService.currentLanguage$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.movieId) {
        this.getMovieCredits();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.viewportWidth = window.innerWidth;
    setTimeout(() => this.checkScroll(), 250);
  }

  @HostListener('window:load')
  load() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.viewportWidth = window.innerWidth;
    setTimeout(() => this.checkScroll(), 250);
  }

  @HostListener('window:resize')
  onResize() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.viewportWidth = window.innerWidth;
    setTimeout(() => this.checkScroll(), 150);
  }

  onImageError(event: Event) {
    return this.utils.onImageVerticalError(event)
  }

  formattedPersonName(sentence: string): string {
    return this.utils.movieNameSplitter(sentence);
  }

  getPersonRoute(credit: any): (string | number)[] {
    const slug = `${credit?.id}-${this.utils.movieNameSplitter(credit?.name || 'person') || 'person'}`;
    return ['/person', slug];
  }


  getMovieCredits(slugParam?: string | null) {
    const slug = slugParam !== undefined ? slugParam : this.route.snapshot.paramMap.get('slug');

    if (slug) {
      const [id, name] = slug.split('-');
      this.movieId = id;
      this.movieName = name;
    }

    if (this.movieId) {
      this.isLoading = true;
      const lang = this.languageService.currentLanguage?.code || 'en-US';
      this.creditService.getCredits(parseInt(this.movieId), 'movie', lang).subscribe({
        next: (data) => {
          this.creditData = data?.cast || [];
          this.isLoading = false;
          setTimeout(() => this.checkScroll(), 200);
        },
        error: () => {
          this.isLoading = false;
        }
      });
    }
  }

  checkScroll(): void {
    if (!this.trendingMovieSection?.nativeElement) return;
    const el = this.trendingMovieSection.nativeElement;
    this.canScrollLeft = el.scrollLeft > 10;
    this.canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 10;
  }

  scrollLeft(): void {
    if (this.trendingMovieSection) {
      this.trendingMovieSection.nativeElement.scrollBy({
        left: -300,
        behavior: 'smooth'
      });
      setTimeout(() => this.checkScroll(), 350);
    }
  }

  scrollRight(): void {
    if (this.trendingMovieSection) {
      this.trendingMovieSection.nativeElement.scrollBy({
        left: 300,
        behavior: 'smooth'
      });
      setTimeout(() => this.checkScroll(), 350);
    }
  }

  onMouseEnter(index: number): void {
    this.hoveredIndex = index;
    this.isBlurred = true;
  }

  onMouseLeave(): void {
    this.hoveredIndex = null;
    this.isBlurred = false;
  }

}